import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { CreditCard, Stethoscope } from "lucide-react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import messages from "../../../../assets/locale/messages";
import useRevalidateOnLocaleChange from "../../../../hooks/useRevalidateOnLocaleChange";
import { companyPermissionModules, genders, lookupsKeys } from "../../../../utils/Constants";
import {
  convertGregorianToHijri,
  convertHijriToGregorian,
  showErrorMsg,
} from "../../../../utils/Helpers";
import { directories, uploadToS3 } from "../../../../utils/S3";
import {
  UIButton,
  UIDatePicker,
  UIDocumentRow,
  UIIcon,
  UIPageFormFooter,
  UIRadioGroup,
  UISelect,
  UITextField,
  // UIProfileCompleteness,
  UIToggle,
  UIUploadProgress,
} from "../../../../components/ui";
import {
  getApprovedCompaniesRequest,
  getCitiesRequest,
  getCountriesRequest,
  getLookupsRequest,
  getSpeakingProficiencyRequest,
} from "../../../../store/Lookups/actions";
import { updateApplicantProfileRequest } from "../../../../store/applicants/actions";
import {
  clearCompanyApplicantProfile,
  getCompanyApplicantProfileRequest,
} from "../../../../store/CompanyPortal/ApplicantProfile/actions";
import {
  CompanyWorkedWithRow,
  ProfileSectionCard,
  ProfileSubsection,
} from "../ViewApplicantProfile/ApplicantProfileLayout";
import ApplicantProfilePageHeader from "../ViewApplicantProfile/ApplicantProfilePageHeader";
import {
  // computeProfileCompleteness,
  getApplicantProfileRoutes,
  getCertificateDocuments,
  isExpiredDate,
  isSaudiNationality,
  readApplicantDisplayName,
  readUserLanguageId,
  readUserLanguageName,
  readUserLanguageProficiencyLabel,
  resolveDocumentUrl,
} from "../ViewApplicantProfile/profileHelpers";
import {
  validateEditApplicantSchema,
  validateSchema,
  validateField,
  isSchemaValid,
} from "../../../../utils/validations";
import { isSuperAdmin, canEditCompanyModule } from "../../../../utils/Permissions";

import "../ViewApplicantProfile/ViewApplicantProfile.scss";
import "./EditApplicantProfile.scss";

const handleFormat = (_date) => {
  const parsedDate = dayjs(_date, "DD-MM-YYYY");
  return parsedDate.format("YYYY-MM-DD");
};

const FIELD_CLASS = "company-applicant-profile__field";
const LTR_VALUE_FIELD_CLASS = `${FIELD_CLASS} company-applicant-profile__field--ltr-value`;
const RTL_VALUE_FIELD_CLASS = `${FIELD_CLASS} company-applicant-profile__field--rtl-value`;
const MAX_DOCUMENT_SIZE = 5 * 1024 * 1024;

const APPLICANT_DOCUMENT_KEYS = {
  cv: "cv_documents",
  certificates: "step_tofel_ielts_documents",
  academic: "academic_qualification_documents",
  other: "other_documents",
};

const formatPendingDocumentSubtitle = (file) => {
  const extension = file.name.split(".").pop()?.toUpperCase() || "PDF";
  const sizeInMb = (file.size / (1024 * 1024)).toFixed(1);
  return `${extension} · ${sizeInMb} Mb`;
};

const syncLookupOptionById = (selected, options) => {
  if (!selected?.id || !Array.isArray(options) || !options.length) {
    return selected;
  }

  return options.find((option) => option.id === selected.id) ?? selected;
};

const syncLookupOptionsByIds = (selectedItems, options) => {
  if (!Array.isArray(selectedItems) || !selectedItems.length) {
    return selectedItems;
  }

  if (!Array.isArray(options) || !options.length) {
    return selectedItems;
  }

  const optionsById = new Map(options.map((option) => [option.id, option]));

  return selectedItems.map((item) => optionsById.get(item?.id) ?? item);
};

const syncProficiencyOption = (selected, options) => {
  if (!selected?.value || !Array.isArray(options) || !options.length) {
    return selected;
  }

  return options.find((option) => option.value === selected.value) ?? selected;
};

// The profile's user_languages entries don't always carry a language id (the
// API may only send a name), so a saved language can't be matched against the
// languages lookup by id alone — fall back to matching by name once the
// lookup loads.
const syncLanguageOption = (selected, options) => {
  if (!selected || !Array.isArray(options) || !options.length) {
    return selected;
  }

  if (selected.id != null) {
    return options.find((option) => option.id === selected.id) ?? selected;
  }

  if (!selected.name) return selected;

  const normalizedName = String(selected.name).trim().toLowerCase();
  const match = options.find(
    (option) => String(option.name || "").trim().toLowerCase() === normalizedName,
  );

  return match ?? selected;
};

// The /enums/speaking_proficiencies lookup doesn't localize its `label` (it
// comes back in English regardless of locale), so proficiency text is
// translated locally instead of trusting it. Which field actually holds the
// matchable enum word (`value`, or just `label`/`name` in English) isn't
// consistent across every place this data comes from, so each candidate is
// tried in turn against the translation map.
const resolveProficiencyLabel = (option, levels) => {
  const fallback = option?.label || option?.name || option?.value || "";
  if (!levels) return fallback;

  const candidates = [option?.value, option?.label, option?.name];
  for (const candidate of candidates) {
    const key = String(candidate || "").trim().toLowerCase();
    if (levels[key]) return levels[key];
  }

  return fallback;
};

const EditApplicantProfile = ({ portalVariant }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const isAdminPortal = portalVariant === "admin-portal";
  const profileRoutes = getApplicantProfileRoutes(portalVariant, id);
  const lang = useSelector((state) => state.locale.lang);
  const { applicants, inputsValidations, shared } = messages[lang];
  const { profile, loading } = useSelector(
    (state) =>
      state.companyApplicantProfile ?? {
        profile: null,
        loading: false,
      },
  );
  const cities = useSelector((state) => state.lookups.cities);
  const countries = useSelector((state) => state.lookups.countries);
  const approvedCompanies = useSelector(
    (state) => state.lookups.approvedCompanies,
  );
  const { roles, fieldOfStudies, disabilities, chronicDiseases, banks, languages } =
    useSelector((state) => state.lookups.general);
  const speakingProficiency = useSelector(
    (state) => state.lookups.speakingProficiency,
  );

  const genderOptions = useMemo(
    () => [
      { id: 0, name: shared.male, value: genders.male },
      { id: 1, name: shared.female, value: genders.female },
      { id: 2, name: shared.notProvided, value: genders.notProvided },
    ],
    [shared.female, shared.male, shared.notProvided],
  );

  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstNameAr, setFirstNameAr] = useState("");
  const [middleNameAr, setMiddleNameAr] = useState("");
  const [lastNameAr, setLastNameAr] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [genderID, setGenderID] = useState(null);
  const [cardHolderName, setCardHolderName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [studyField, setStudyField] = useState({});
  const [fieldStudyName, setFieldStudyName] = useState(null);
  const [hijriDob, setHijriDob] = useState("");
  const [gregorianDob, setGregorianDob] = useState("");
  const [city, setCity] = useState({});
  const [iban, setIban] = useState(null);
  const [height, setHeight] = useState(null);
  const [weight, setWeight] = useState(null);
  const [iqmaExpireDate, setIqmaExpireDate] = useState("");
  const [iqmaNumber, setIqmaNumber] = useState("");
  const [matloobNumber, setMatloobNumber] = useState("");
  const [nationalExpireDate, setNationalExpireDate] = useState("");
  const [nationality, setNationality] = useState({});
  const [phoneNumber, setPhoneNumber] = useState("");
  const [rating, setRating] = useState("");
  const [stcNumber, setStcNumber] = useState("");
  const [role, setRole] = useState({});
  const [bank, setBank] = useState({});
  const [favouriteCompany, setFavouriteCompany] = useState({});
  const [isNotifiable, setIsNotifiable] = useState(null);
  const [disability, setDisability] = useState([]);
  const [chronic, setChronic] = useState([]);
  const [hasDisability, setHasDisability] = useState(false);
  const [hasChronic, setHasChronic] = useState(false);
  const [nationalID, setNationalID] = useState(null);
  const [userLanguages, setUserLanguages] = useState([]);
  const languageRowIdRef = useRef(0);
  const [updateHijri, setUpdateHijri] = useState("");
  const [formHydrated, setFormHydrated] = useState(false);
  const [pendingDocs, setPendingDocs] = useState({});
  const [documentErrors, setDocumentErrors] = useState({});
  const [uploadState, setUploadState] = useState({ target: null, percent: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pendingDocsRef = useRef(pendingDocs);

  useEffect(() => {
    pendingDocsRef.current = pendingDocs;
  }, [pendingDocs]);

  const inputDateFormat = lang === "ar" ? "YYYY-MM-DD" : "MM-DD-YYYY";
  const canEdit = isAdminPortal
    ? isSuperAdmin()
    : canEditCompanyModule(companyPermissionModules.applicants);
  // Company worked with drives applicant loyalty — super admin only.
  const canEditFavouriteCompany = isSuperAdmin();
  const viewApplicantPath = profileRoutes.view;
  const copy = applicants.companyApplicantProfile;
  const profileLabels = applicants.applicantDetails.applicantProfile.labels;
  const basicLabels = profileLabels.basicInfo;
  const educationLabels = profileLabels.educationInfo;
  const bankInfoLabels = profileLabels.bankInfo;
  const iqamaExpiredMessage = copy.iqamaExpired;
  const isSaudi = isSaudiNationality(nationality);
  // Only meaningful for non-Saudi applicants — the Iqama fields are hidden for Saudis,
  // and a Saudi profile can still carry a stale iqama_expiration_date from the server.
  const isIqamaExpired = useMemo(
    () => !isSaudi && isExpiredDate(iqmaExpireDate),
    [isSaudi, iqmaExpireDate],
  );
  const certificates = profile ? getCertificateDocuments(profile) : [];
  const selectedLanguageIds = new Set(
    userLanguages
      .filter((row) => !row._destroy && row.language?.id != null)
      .map((row) => row.language.id),
  );
  // The backend rejects saves with "Age must be in 16..40" — disable Date of Birth
  // selections that would violate that instead of letting the user pick one and only
  // finding out on submit.
  const dobMinDate = useMemo(
    () => dayjs().subtract(40, "year").format("YYYY-MM-DD"),
    [],
  );
  const dobMaxDate = useMemo(
    () => dayjs().subtract(16, "year").format("YYYY-MM-DD"),
    [],
  );

  useEffect(() => {
    if (!canEdit) {
      history.replace(viewApplicantPath);
    }
  }, [canEdit, history, viewApplicantPath]);

  useEffect(() => {
    dispatch(
      getLookupsRequest([
        lookupsKeys.roles,
        lookupsKeys.fieldOfStudies,
        lookupsKeys.disabilities,
        lookupsKeys.chronicDiseases,
        lookupsKeys.banks,
        lookupsKeys.languages,
      ]),
    );
  }, [dispatch, lang]);

  useEffect(() => {
    if (!canEditFavouriteCompany) return;
    dispatch(getApprovedCompaniesRequest());
  }, [canEditFavouriteCompany, dispatch, lang]);

  useEffect(() => {
    dispatch(getSpeakingProficiencyRequest());
  }, [dispatch, lang]);

  useEffect(() => {
    dispatch(
      getCitiesRequest({
        page: -1,
        order_by_name_en: lang === "en" ? "asc" : null,
        order_by_name_ar: lang === "ar" ? "asc" : null,
      }),
    );
  }, [dispatch, lang]);

  useEffect(() => {
    dispatch(getCountriesRequest({ page: -1 }));
  }, [dispatch, lang]);

  useEffect(() => {
    if (!id) return undefined;
    dispatch(
      getCompanyApplicantProfileRequest({
        id,
        portal: isAdminPortal ? "admin" : "company",
      }),
    );
    return () => {
      dispatch(clearCompanyApplicantProfile());
    };
  }, [dispatch, id, isAdminPortal]);

  useEffect(() => {
    if (!profile || formHydrated) return;

    setGregorianDob(profile?.gregorian_date_of_birth);
    setHijriDob(convertHijriToGregorian(profile?.hijri_date_of_birth));
    setFirstName(profile?.first_name_en || profile?.first_name || "");
    setMiddleName(profile?.middle_name_en || profile?.middle_name || "");
    setLastName(profile?.last_name_en || profile?.last_name || "");
    setFirstNameAr(profile?.first_name_ar || profile?.first_name || "");
    setMiddleNameAr(profile?.middle_name_ar || profile?.middle_name || "");
    setLastNameAr(profile?.last_name_ar || profile?.last_name || "");
    setCardHolderName(profile?.card_holder_name);
    setCountryCode(profile?.country_code);
    setStudyField(profile?.field_of_study || {});
    setHeight(profile?.height);
    setWeight(profile?.weight);
    setIban(profile?.iban);
    setIqmaExpireDate(profile?.iqama_expiration_date);
    setIqmaNumber(profile?.iqama_id);
    setMatloobNumber(profile?.matloob_number);
    setNationalExpireDate(
      profile?.national_id_expiration_date &&
        handleFormat(profile?.national_id_expiration_date),
    );
    setNationality(profile?.nationality || {});
    setPhoneNumber(profile?.phone_number);
    setRating(profile?.rating);
    setRole(profile?.role || {});
    setStcNumber(profile?.stc_number);
    setBank(profile?.bank || {});
    setFavouriteCompany(profile?.favourite_company || {});
    setCity(profile?.city || {});
    setDisability(profile?.disabilities || []);
    setChronic(profile?.chronic_diseases || []);
    setHasDisability((profile?.disabilities?.length ?? 0) > 0);
    setHasChronic((profile?.chronic_diseases?.length ?? 0) > 0);
    setFieldStudyName(profile?.field_of_study_name);
    setNationalID(profile?.national_id);
    setIsNotifiable(profile?.is_notifiable);
    setUserLanguages(
      (profile?.user_languages || []).map((item) => ({
        key: `existing-${item.id}`,
        id: item.id,
        language: { id: readUserLanguageId(item), name: readUserLanguageName(item) },
        proficiency: {
          value: item.speaking_proficiency,
          label: readUserLanguageProficiencyLabel(item, lang),
        },
        _destroy: false,
      })),
    );

    const matchedGender = genderOptions.find(
      (option) =>
        option.value.toLowerCase() === String(profile?.gender || "").toLowerCase(),
    );
    setGenderID(
      matchedGender?.id ?? genderOptions[genderOptions.length - 1]?.id,
    );
    setValidationErrors({});
    setFormHydrated(true);
  }, [formHydrated, genderOptions, profile]);

  useEffect(() => {
    if (!formHydrated) return;

    setStudyField((prev) => syncLookupOptionById(prev, fieldOfStudies));
    setDisability((prev) => syncLookupOptionsByIds(prev, disabilities));
    setChronic((prev) => syncLookupOptionsByIds(prev, chronicDiseases));
    setRole((prev) => syncLookupOptionById(prev, roles));
    setBank((prev) => syncLookupOptionById(prev, banks));
    setFavouriteCompany((prev) => syncLookupOptionById(prev, approvedCompanies));
    setCity((prev) => syncLookupOptionById(prev, cities));
    setNationality((prev) => syncLookupOptionById(prev, countries));
    setUserLanguages((prev) =>
      prev.map((row) => ({
        ...row,
        language: syncLanguageOption(row.language, languages),
        proficiency: syncProficiencyOption(row.proficiency, speakingProficiency),
      })),
    );
  }, [
    formHydrated,
    lang,
    fieldOfStudies,
    disabilities,
    chronicDiseases,
    roles,
    banks,
    approvedCompanies,
    cities,
    countries,
    languages,
    speakingProficiency,
  ]);

  useEffect(() => {
    setUpdateHijri(convertGregorianToHijri(hijriDob));
  }, [hijriDob]);

  const displayName = readApplicantDisplayName(profile, lang);

  // const draftProfile = useMemo(() => {
  //   const withPendingDocuments = (key, existingDocuments) => {
  //     if (pendingDocs[key] instanceof File) {
  //       return ["pending"];
  //     }
  //     return existingDocuments;
  //   };

  //   return {
  //     ...profile,
  //     nationality,
  //     iqama_id: iqmaNumber,
  //     iqama_expiration_date: iqmaExpireDate,
  //     cv_documents: withPendingDocuments(
  //       APPLICANT_DOCUMENT_KEYS.cv,
  //       profile?.cv_documents,
  //     ),
  //     academic_qualification_documents: withPendingDocuments(
  //       APPLICANT_DOCUMENT_KEYS.academic,
  //       profile?.academic_qualification_documents,
  //     ),
  //     safety_and_security_documents: withPendingDocuments(
  //       APPLICANT_DOCUMENT_KEYS.certificates,
  //       profile?.safety_and_security_documents,
  //     ),
  //     step_tofel_ielts_documents: profile?.step_tofel_ielts_documents,
  //     other_documents: withPendingDocuments(
  //       APPLICANT_DOCUMENT_KEYS.other,
  //       profile?.other_documents,
  //     ),
  //   };
  // }, [iqmaExpireDate, iqmaNumber, nationality, pendingDocs, profile]);

  // const completeness = useMemo(
  //   () =>
  //     computeProfileCompleteness(draftProfile, {
  //       certificates: profileLabels.documentInfo.certificates,
  //       academicQualifications: profileLabels.documentInfo.academicQualifications,
  //       cv: profileLabels.documentInfo.cv,
  //       iqama: profileLabels.basicInfo.iqamaNumber,
  //       iqamaExpired: copy.iqamaExpired,
  //     }),
  //   [copy.iqamaExpired, draftProfile, profileLabels],
  // );

  const isStudyFieldOther = studyField?.name === "Other";

  const editApplicantSchemaOptions = {
    isSaudi,
    hasChronic,
    hasDisability,
    isStudyFieldOther,
  };

  const editApplicantFieldValues = {
    firstName,
    middleName,
    lastName,
    firstNameAr,
    middleNameAr,
    lastNameAr,
    iban,
    countryCode: countryCode ?? "",
    phoneNumber: phoneNumber ?? "",
    matloobNumber: matloobNumber ?? "",
    height: height ?? "",
    weight: weight ?? "",
    stcNumber: stcNumber ?? "",
    cardHolderName: cardHolderName ?? "",
    nationalityId: nationality?.id ?? "",
    cityId: city?.id ?? "",
    roleId: role?.id ?? "",
    bankId: bank?.id ?? "",
    studyFieldId: studyField?.id ?? "",
    isNotifiable,
    nationalId: nationalID ?? "",
    nationalExpireDate: nationalExpireDate ?? "",
    iqamaNumber: iqmaNumber ?? "",
    iqamaExpireDate: iqmaExpireDate ?? "",
    fieldStudyName: fieldStudyName ?? "",
    chronic,
    disability,
  };

  const validateEditApplicantFields = () =>
    validateSchema(
      validateEditApplicantSchema(editApplicantSchemaOptions),
      editApplicantFieldValues,
      inputsValidations,
    );

  const handleFieldValidation = (fieldName, value) => {
    const error = validateField(
      validateEditApplicantSchema(editApplicantSchemaOptions),
      fieldName,
      value,
      inputsValidations,
    );
    setValidationErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  const isFormValid = isSchemaValid(
    validateEditApplicantSchema(editApplicantSchemaOptions),
    editApplicantFieldValues,
  );
  const hasIncompleteLanguageRow = userLanguages.some(
    (row) =>
      !row._destroy &&
      (row.language?.id == null || row.proficiency?.value == null),
  );

  // Re-translate any error text already on screen when the UI language changes —
  // only fields that already show an error get their message re-derived; fields
  // the user hasn't touched yet stay hidden, same as before the language switch.
  useRevalidateOnLocaleChange(() => {
    setValidationErrors((prev) => {
      const next = { ...prev };
      Object.keys(prev).forEach((fieldName) => {
        if (!prev[fieldName]) return;
        next[fieldName] = validateField(
          validateEditApplicantSchema(editApplicantSchemaOptions),
          fieldName,
          editApplicantFieldValues[fieldName],
          inputsValidations,
        );
      });
      return next;
    });
  });

  // Surface any already-invalid data (e.g. empty required Arabic name fields coming
  // straight from the server) as soon as the form loads. Without this, a field the
  // user never touched can silently keep Save disabled with no visible indication of
  // which field is the problem.
  useEffect(() => {
    if (!formHydrated) return;
    setValidationErrors(validateEditApplicantFields());
  }, [formHydrated]);

  const uploadApplicantDocument = async (file, docKey) => {
    const ext = file.name.split(".").pop() || "pdf";
    const fileName = `${docKey}.${ext}`;

    setUploadState({ target: docKey, percent: 0 });

    const uploaded = await uploadToS3(file, fileName, directories.applicants, {
      onProgress: (percent) => setUploadState({ target: docKey, percent }),
    });

    return uploaded?.link ?? null;
  };

  const handleAddLanguageRow = () => {
    languageRowIdRef.current += 1;
    // Default a newly added row to "Intermediate" — Proficiency stays disabled
    // until a language is picked, so this is just the value it starts with.
    const defaultProficiency = (speakingProficiency || []).find(
      (option) => option.value === "intermediate",
    ) || { value: "intermediate", label: "Intermediate" };
    setUserLanguages((prev) => [
      ...prev,
      {
        key: `new-${languageRowIdRef.current}`,
        id: undefined,
        language: null,
        proficiency: defaultProficiency,
        _destroy: false,
      },
    ]);
  };

  const handleRemoveLanguageRow = (key) => {
    setUserLanguages((prev) =>
      prev.reduce((acc, row) => {
        if (row.key !== key) {
          acc.push(row);
        } else if (row.id) {
          // Existing rows are soft-deleted so the id can be sent with _destroy.
          acc.push({ ...row, _destroy: true });
        }
        return acc;
      }, []),
    );
  };

  const handleLanguageRowChange = (key, field, value) => {
    setUserLanguages((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );
  };

  const handleDocumentSelect = (docKey) => (file) => {
    if (file.type !== "application/pdf") {
      setDocumentErrors((prev) => ({
        ...prev,
        [docKey]: shared.pdfUploadTypeError,
      }));
      return;
    }

    if (file.size > MAX_DOCUMENT_SIZE) {
      setDocumentErrors((prev) => ({
        ...prev,
        [docKey]: shared.pdfUploadSizeError,
      }));
      return;
    }

    setDocumentErrors((prev) => ({ ...prev, [docKey]: "" }));
    setPendingDocs((prev) => ({ ...prev, [docKey]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateEditApplicantFields();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0 || isIqamaExpired) {
      return;
    }

    setIsSubmitting(true);

    try {
      const pendingDocsNow = pendingDocsRef.current;
      const documentPayload = {};

      for (const [docKey, file] of Object.entries(pendingDocsNow)) {
        if (!(file instanceof File)) continue;

        const uploadedUrl = await uploadApplicantDocument(file, docKey);
        if (!uploadedUrl) {
          showErrorMsg(copy.documentUploadFailed);
          return;
        }

        documentPayload[docKey] = [uploadedUrl];
      }

      const userLanguagesAttributes = userLanguages.reduce((acc, row) => {
        if (row._destroy) {
          if (row.id) acc.push({ id: row.id, _destroy: true });
          return acc;
        }
        if (row.language?.id && row.proficiency?.value) {
          acc.push({
            ...(row.id ? { id: row.id } : {}),
            language_id: row.language.id,
            speaking_proficiency: row.proficiency.value,
            _destroy: false,
          });
        }
        return acc;
      }, []);

      dispatch(
        updateApplicantProfileRequest({
          id,
          portalVariant,
          data: {
            first_name_en: firstName,
            middle_name_en: middleName,
            last_name_en: lastName,
            first_name_ar: firstNameAr,
            middle_name_ar: middleNameAr,
            last_name_ar: lastNameAr,
            gender: genderID,
            card_holder_name: cardHolderName,
            dial_code: countryCode,
            field_of_study_id: studyField?.id,
            hijri_date_of_birth: updateHijri,
            gregorian_date_of_birth: gregorianDob,
            iqama_expiration_date: isSaudi ? null : iqmaExpireDate,
            iqama_id: isSaudi ? null : iqmaNumber,
            matloob_number: matloobNumber,
            national_id_expiration_date: isSaudi ? nationalExpireDate : null,
            national_id: isSaudi ? nationalID : null,
            phone_number: phoneNumber,
            disability_ids: hasDisability
              ? disability.map((item) => item.id)
              : [],
            stc_number: stcNumber,
            chronic_disease_ids: hasChronic ? chronic.map((item) => item.id) : [],
            field_of_study_name: fieldStudyName,
            role_id: role?.id,
            is_notifiable: isNotifiable,
            iban,
            height,
            weight,
            nationality_id: nationality?.id,
            rating,
            bank_id: bank?.id,
            ...(canEditFavouriteCompany
              ? { favourite_company_id: favouriteCompany?.id ?? null }
              : {}),
            city_id: city?.id,
            user_languages_attributes: userLanguagesAttributes,
            ...documentPayload,
          },
        }),
      );
    } catch (error) {
      showErrorMsg(error?.message || copy.documentUploadFailed);
    } finally {
      setUploadState({ target: null, percent: 0 });
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    history.push(viewApplicantPath);
  };

  const selectedGender =
    genderOptions.find((option) => option.id === genderID) || null;

  const breadcrumbList = [
    {
      label: isAdminPortal
        ? applicants.listApplicants.listApplicantsBreadcrumb
        : copy.breadcrumbApplicants,
      path: profileRoutes.applicantsList,
    },
    {
      label: displayName || copy.profileTitle,
      path: viewApplicantPath,
    },
    {
      label: applicants.applicantDetails.editProfilePageTitle,
      path: "",
    },
  ];

  if (!canEdit) {
    return null;
  }

  const isUploadingDocuments = uploadState.target != null;
  const uploadProgressLabel =
    uploadState.target != null
      ? `${messages[lang].company.uploadFile.uploading} ${uploadState.percent}%`
      : undefined;

  const renderApplicantDocumentRow = ({
    title,
    docKey,
    existingDoc,
    statusText,
    statusTone = "muted",
  }) => {
    const pendingFile = pendingDocs[docKey];
    const href = resolveDocumentUrl(existingDoc);
    const hasExisting = Boolean(href);
    const hasPending = pendingFile instanceof File;

    return (
      <React.Fragment key={docKey}>
        <UIDocumentRow
          title={title}
          subtitle={
            hasPending
              ? formatPendingDocumentSubtitle(pendingFile)
              : hasExisting
                ? copy.documentAvailable
                : undefined
          }
          statusText={hasExisting || hasPending ? "" : statusText}
          statusTone={statusTone}
          href={href}
          downloadLabel={copy.downloadDocument}
          uploadLabel={copy.uploadDocument}
          onFileSelect={!hasExisting ? handleDocumentSelect(docKey) : undefined}
          uploadDisabled={isSubmitting || isUploadingDocuments}
        />
        {documentErrors[docKey] ? (
          <Typography
            variant="caption"
            sx={{ color: "error.main", display: "block", mt: 0.5 }}
          >
            {documentErrors[docKey]}
          </Typography>
        ) : null}
        {uploadState.target === docKey ? (
          <UIUploadProgress
            fileName={pendingFile?.name || title}
            isPdf
            progress={uploadState.percent}
            label={uploadProgressLabel}
          />
        ) : null}
      </React.Fragment>
    );
  };

  if (loading && !profile) {
    return (
      <Box className="company-applicant-profile">
        <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />
        <div className="company-applicant-profile__loading" aria-live="polite">
          {shared.loading}
        </div>
      </Box>
    );
  }

  return (
    <Box className="company-applicant-profile edit-applicant-page">
      <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />

      <ApplicantProfilePageHeader
        applicantId={id}
        portalVariant={portalVariant}
        profile={profile}
        showEditButton={false}
      />

      <form onSubmit={handleSubmit}>
        <div className="company-applicant-profile__grid">
          <div className="company-applicant-profile__column">
            <ProfileSectionCard title={copy.contactPerson}>
              <ProfileSubsection title={copy.identity} divider={false}>
                <UITextField
                  className={FIELD_CLASS}
                  label={basicLabels.firstName}
                  required
                  name="firstName"
                  placeholder={applicants.placeholders.enterFirstName}
                  value={firstName}
                  onValueChange={(value) => {
                    setFirstName(value);
                    handleFieldValidation("firstName", value);
                  }}
                  onBlur={(e) => handleFieldValidation("firstName", e.target.value)}
                  error={Boolean(validationErrors.firstName)}
                  helperText={validationErrors.firstName}
                />
                <UITextField
                  className={FIELD_CLASS}
                  label={basicLabels.middleName}
                  required
                  name="middleName"
                  placeholder={applicants.placeholders.enterMiddleName}
                  value={middleName}
                  onValueChange={(value) => {
                    setMiddleName(value);
                    handleFieldValidation("middleName", value);
                  }}
                  onBlur={(e) =>
                    handleFieldValidation("middleName", e.target.value)
                  }
                  error={Boolean(validationErrors.middleName)}
                  helperText={validationErrors.middleName}
                />
                <UITextField
                  className={FIELD_CLASS}
                  label={basicLabels.lastName}
                  required
                  name="lastName"
                  placeholder={applicants.placeholders.enterLastName}
                  value={lastName}
                  onValueChange={(value) => {
                    setLastName(value);
                    handleFieldValidation("lastName", value);
                  }}
                  onBlur={(e) => handleFieldValidation("lastName", e.target.value)}
                  error={Boolean(validationErrors.lastName)}
                  helperText={validationErrors.lastName}
                />
                <UITextField
                  className={RTL_VALUE_FIELD_CLASS}
                  label={basicLabels.firstNameAr}
                  required
                  name="firstNameAr"
                  placeholder={applicants.placeholders.enterFirstNameAr}
                  value={firstNameAr}
                  inputProps={{ dir: "rtl" }}
                  onValueChange={(value) => {
                    setFirstNameAr(value);
                    handleFieldValidation("firstNameAr", value);
                  }}
                  onBlur={(e) =>
                    handleFieldValidation("firstNameAr", e.target.value)
                  }
                  error={Boolean(validationErrors.firstNameAr)}
                  helperText={validationErrors.firstNameAr}
                />
                <UITextField
                  className={RTL_VALUE_FIELD_CLASS}
                  label={basicLabels.middleNameAr}
                  required
                  name="middleNameAr"
                  placeholder={applicants.placeholders.enterMiddleNameAr}
                  value={middleNameAr}
                  inputProps={{ dir: "rtl" }}
                  onValueChange={(value) => {
                    setMiddleNameAr(value);
                    handleFieldValidation("middleNameAr", value);
                  }}
                  onBlur={(e) =>
                    handleFieldValidation("middleNameAr", e.target.value)
                  }
                  error={Boolean(validationErrors.middleNameAr)}
                  helperText={validationErrors.middleNameAr}
                />
                <UITextField
                  className={RTL_VALUE_FIELD_CLASS}
                  label={basicLabels.lastNameAr}
                  required
                  name="lastNameAr"
                  placeholder={applicants.placeholders.enterLastNameAr}
                  value={lastNameAr}
                  inputProps={{ dir: "rtl" }}
                  onValueChange={(value) => {
                    setLastNameAr(value);
                    handleFieldValidation("lastNameAr", value);
                  }}
                  onBlur={(e) => handleFieldValidation("lastNameAr", e.target.value)}
                  error={Boolean(validationErrors.lastNameAr)}
                  helperText={validationErrors.lastNameAr}
                />
                <UISelect
                  className={FIELD_CLASS}
                  label={basicLabels.nationality}
                  required
                  name="nationality"
                  placeholder={applicants.placeholders.selectNationality}
                  options={countries || []}
                  value={nationality?.id != null ? nationality : null}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(option) => {
                    setNationality(option || {});
                    if (option?.dial_code) setCountryCode(option.dial_code);
                    handleFieldValidation("nationalityId", option?.id ?? "");
                  }}
                  error={Boolean(validationErrors.nationalityId)}
                  helperText={validationErrors.nationalityId}
                />
                <UISelect
                  className={FIELD_CLASS}
                  label={basicLabels.cityOfResidence}
                  required
                  name="city"
                  placeholder={applicants.placeholders.selectCity}
                  options={cities || []}
                  value={city?.id != null ? city : null}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(option) => {
                    setCity(option || {});
                    handleFieldValidation("cityId", option?.id ?? "");
                  }}
                  error={Boolean(validationErrors.cityId)}
                  helperText={validationErrors.cityId}
                />
                <UITextField
                  className={LTR_VALUE_FIELD_CLASS}
                  label={basicLabels.countryCode}
                  required
                  name="countryCode"
                  placeholder={applicants.placeholders.enterCountryCode}
                  value={countryCode ?? ""}
                  onValueChange={(value) => {
                    setCountryCode(value);
                    handleFieldValidation("countryCode", value);
                  }}
                  onBlur={(e) => handleFieldValidation("countryCode", e.target.value)}
                  error={Boolean(validationErrors.countryCode)}
                  helperText={validationErrors.countryCode}
                />
                <UITextField
                  className={LTR_VALUE_FIELD_CLASS}
                  label={basicLabels.phoneNumber}
                  required
                  name="phoneNumber"
                  placeholder={applicants.placeholders.enterPhoneNumber}
                  value={phoneNumber ?? ""}
                  onValueChange={(value) => {
                    setPhoneNumber(value);
                    handleFieldValidation("phoneNumber", value);
                  }}
                  onBlur={(e) => handleFieldValidation("phoneNumber", e.target.value)}
                  error={Boolean(validationErrors.phoneNumber)}
                  helperText={validationErrors.phoneNumber}
                />
                <UITextField
                  className={LTR_VALUE_FIELD_CLASS}
                  label={basicLabels.matlooobNumber}
                  name="matloobNumber"
                  placeholder={applicants.placeholders.enterMatloobNumber}
                  value={matloobNumber ?? ""}
                  onValueChange={(value) => {
                    setMatloobNumber(value);
                    handleFieldValidation("matloobNumber", value);
                  }}
                  onBlur={(e) =>
                    handleFieldValidation("matloobNumber", e.target.value)
                  }
                  error={Boolean(validationErrors.matloobNumber)}
                  helperText={validationErrors.matloobNumber}
                />
                <UISelect
                  className={FIELD_CLASS}
                  label={basicLabels.role}
                  required
                  name="role"
                  placeholder={applicants.placeholders.selectRole}
                  options={roles || []}
                  value={role?.id != null ? role : null}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(option) => {
                    setRole(option || {});
                    handleFieldValidation("roleId", option?.id ?? "");
                  }}
                  error={Boolean(validationErrors.roleId)}
                  helperText={validationErrors.roleId}
                />
                <UISelect
                  className={FIELD_CLASS}
                  label={basicLabels.gender}
                  name="gender"
                  placeholder={applicants.placeholders.selectGender}
                  options={genderOptions}
                  value={selectedGender}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(option) => setGenderID(option?.id ?? null)}
                />
                {isSaudi ? (
                  <>
                    <UITextField
                      className={LTR_VALUE_FIELD_CLASS}
                      label={basicLabels.nationalId}
                      required
                      name="nationalId"
                      placeholder={applicants.placeholders.enterNationalId}
                      value={nationalID ?? ""}
                      onValueChange={(value) => {
                        setNationalID(value);
                        handleFieldValidation("nationalId", value);
                      }}
                      onBlur={(e) =>
                        handleFieldValidation("nationalId", e.target.value)
                      }
                      error={Boolean(validationErrors.nationalId)}
                      helperText={validationErrors.nationalId}
                    />
                    <UIDatePicker
                      className={FIELD_CLASS}
                      name="expireDate"
                      id="expireDate"
                      label={basicLabels.expiryDate}
                      required
                      placeholder={
                        applicants.placeholders.nationalIdExpireDatePlaceholder
                      }
                      value={nationalExpireDate}
                      onChange={(next) => {
                        const value = next ? dayjs(next).format() : "";
                        setNationalExpireDate(value);
                        handleFieldValidation("nationalExpireDate", value);
                      }}
                      inputFormat={inputDateFormat}
                      openTo="year"
                      views={["year", "month", "day"]}
                      error={Boolean(validationErrors.nationalExpireDate)}
                      helperText={validationErrors.nationalExpireDate}
                    />
                  </>
                ) : null}
              </ProfileSubsection>

              <ProfileSubsection title={copy.dateOfBirth}>
                <UIDatePicker
                  className={FIELD_CLASS}
                  name="dobhijri"
                  id="dobhijri"
                  label={basicLabels.dateOfBirthHijri}
                  placeholder={applicants.placeholders.selectDateOfBirthPlaceholder}
                  calendarSystem="hijri"
                  value={hijriDob}
                  onChange={(next) => {
                    setHijriDob(next);
                    // Keep the Gregorian field in sync live — the backend only reads
                    // gregorian_date_of_birth (it derives Hijri from it server-side),
                    // so this is also what makes editing the Hijri field actually save.
                    setGregorianDob(next ? dayjs(next).format() : "");
                  }}
                  minDate={dobMinDate}
                  maxDate={dobMaxDate}
                  openTo="year"
                  views={["year", "month", "day"]}
                />
                <UIDatePicker
                  className={FIELD_CLASS}
                  name="dobGregorian"
                  id="dobGregorian"
                  label={basicLabels.dateOfBirthGregorian}
                  placeholder={applicants.placeholders.selectDateOfBirthPlaceholder}
                  value={gregorianDob}
                  onChange={(next) => {
                    setGregorianDob(next ? dayjs(next).format() : "");
                    // Keep the Hijri field in sync live for the same reason, reversed.
                    setHijriDob(next || "");
                  }}
                  minDate={dobMinDate}
                  maxDate={dobMaxDate}
                  openTo="year"
                  views={["year", "month", "day"]}
                />
              </ProfileSubsection>

              <ProfileSubsection title={copy.physicalDetails}>
                <UITextField
                  className={LTR_VALUE_FIELD_CLASS}
                  label={`${basicLabels.height} (${shared.cm})`}
                  required
                  name="height"
                  placeholder={applicants.placeholders.enterHeight}
                  value={height ?? ""}
                  onValueChange={(value) => {
                    setHeight(value);
                    handleFieldValidation("height", value);
                  }}
                  onBlur={(e) => handleFieldValidation("height", e.target.value)}
                  error={Boolean(validationErrors.height)}
                  helperText={validationErrors.height}
                />
                <UITextField
                  className={LTR_VALUE_FIELD_CLASS}
                  label={`${basicLabels.weight} (${shared.kg})`}
                  required
                  name="weight"
                  placeholder={applicants.placeholders.enterWeight}
                  value={weight ?? ""}
                  onValueChange={(value) => {
                    setWeight(value);
                    handleFieldValidation("weight", value);
                  }}
                  onBlur={(e) => handleFieldValidation("weight", e.target.value)}
                  error={Boolean(validationErrors.weight)}
                  helperText={validationErrors.weight}
                />
              </ProfileSubsection>

              {!isSaudi ? (
                <ProfileSubsection title={copy.residencyId}>
                  <UITextField
                    className={LTR_VALUE_FIELD_CLASS}
                    label={basicLabels.iqamaNumber}
                    required
                    name="iqamaNumber"
                    placeholder={applicants.placeholders.enterIqamaNumber}
                    value={iqmaNumber ?? ""}
                    onValueChange={(value) => {
                      setIqmaNumber(value);
                      handleFieldValidation("iqamaNumber", value);
                    }}
                    onBlur={(e) =>
                      handleFieldValidation("iqamaNumber", e.target.value)
                    }
                    error={Boolean(validationErrors.iqamaNumber)}
                    helperText={validationErrors.iqamaNumber}
                  />
                  <UIDatePicker
                    className={FIELD_CLASS}
                    name="iqamaexpiredate"
                    id="iqamaexpiredate"
                    label={basicLabels.iqmaExpireDate}
                    required
                    placeholder={applicants.placeholders.selectIqamaExpiryDate}
                    value={iqmaExpireDate}
                    onChange={(next) => {
                      const value = next ? dayjs(next).format() : "";
                      setIqmaExpireDate(value);
                      handleFieldValidation("iqamaExpireDate", value);
                    }}
                    inputFormat={inputDateFormat}
                    openTo="year"
                    views={["year", "month", "day"]}
                    disablePast
                    error={
                      isIqamaExpired || Boolean(validationErrors.iqamaExpireDate)
                    }
                    helperText={
                      validationErrors.iqamaExpireDate ||
                      (isIqamaExpired ? iqamaExpiredMessage : undefined)
                    }
                  />
                </ProfileSubsection>
              ) : null}

              <ProfileSubsection grid={false}>
                <CompanyWorkedWithRow label={copy.companyWorkedWith}>
                  {canEditFavouriteCompany ? (
                    <UISelect
                      className="company-applicant-profile__company-select"
                      name="favouriteCompany"
                      placeholder={applicants.placeholders.selectCompanyWorkedWith}
                      options={approvedCompanies || []}
                      value={favouriteCompany?.id != null ? favouriteCompany : null}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onChange={(option) => setFavouriteCompany(option || {})}
                    />
                  ) : (
                    <span className="company-applicant-profile__company-value">
                      {favouriteCompany?.name || shared.none}
                    </span>
                  )}
                </CompanyWorkedWithRow>
              </ProfileSubsection>
            </ProfileSectionCard>

            <ProfileSectionCard title={copy.healthAccessibility}>
              <div className="company-applicant-profile__health">
                <div className="company-applicant-profile__health-block">
                  <div className="company-applicant-profile__health-toggle">
                    <div
                      className="company-applicant-profile__bank-icon-box"
                      aria-hidden
                    >
                      <Stethoscope size={18} strokeWidth={1.75} />
                    </div>
                    <h4 className="company-applicant-profile__health-title">
                      {copy.hasMedicalCondition}
                    </h4>
                    <div className="company-applicant-profile__toggle-control">
                      <UIToggle
                        name="hasChronic"
                        checked={hasChronic}
                        onChange={setHasChronic}
                      />
                    </div>
                  </div>
                  {hasChronic ? (
                    <UISelect
                      className={FIELD_CLASS}
                      label={copy.medicalConditionDetails}
                      required
                      name="chronicDisease"
                      placeholder={applicants.placeholders.selectMedicalCondition}
                      multiple
                      disableCloseOnSelect
                      options={chronicDiseases || []}
                      value={chronic}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onChange={(nextValue) => {
                        const next = nextValue || [];
                        setChronic(next);
                        handleFieldValidation("chronic", next);
                      }}
                      renderTags={(selected) =>
                        selected
                          .map((option) => option?.name)
                          .filter(Boolean)
                          .join(", ")
                      }
                      error={Boolean(validationErrors.chronic)}
                      helperText={validationErrors.chronic}
                    />
                  ) : (
                    <div className="company-applicant-profile__health-readonly">
                      <span className="company-applicant-profile__meta-label">
                        {copy.medicalConditionDetails}
                      </span>
                      <span className="company-applicant-profile__meta-value">
                        {copy.noMedicalCondition}
                      </span>
                    </div>
                  )}
                </div>

                <div className="company-applicant-profile__health-block">
                  <div className="company-applicant-profile__health-toggle">
                    <div
                      className="company-applicant-profile__bank-icon-box"
                      aria-hidden
                    >
                      <Stethoscope size={18} strokeWidth={1.75} />
                    </div>
                    <h4 className="company-applicant-profile__health-title">
                      {copy.hasDisability}
                    </h4>
                    <div className="company-applicant-profile__toggle-control">
                      <UIToggle
                        name="hasDisability"
                        checked={hasDisability}
                        onChange={setHasDisability}
                      />
                    </div>
                  </div>
                  {hasDisability ? (
                    <UISelect
                      className={FIELD_CLASS}
                      label={copy.disabilityDetails}
                      required
                      name="disability"
                      placeholder={applicants.placeholders.selectDisability}
                      multiple
                      disableCloseOnSelect
                      options={disabilities || []}
                      value={disability}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onChange={(nextValue) => {
                        const next = nextValue || [];
                        setDisability(next);
                        handleFieldValidation("disability", next);
                      }}
                      renderTags={(selected) =>
                        selected
                          .map((option) => option?.name)
                          .filter(Boolean)
                          .join(", ")
                      }
                      error={Boolean(validationErrors.disability)}
                      helperText={validationErrors.disability}
                    />
                  ) : (
                    <div className="company-applicant-profile__health-readonly">
                      <span className="company-applicant-profile__meta-label">
                        {copy.disabilityDetails}
                      </span>
                      <span className="company-applicant-profile__meta-value">
                        {copy.noDisability}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </ProfileSectionCard>
          </div>

          <div className="company-applicant-profile__column">
            {/* <ProfileSectionCard title={copy.profileCompleteness}>
              <UIProfileCompleteness
                percent={completeness.percent}
                missingFields={completeness.missingFields}
                completeLabel={copy.completeStatus}
                fieldsMissingLabel={
                  completeness.missingCount > 0
                    ? copy.fieldsMissing.replace(
                        "{count}",
                        completeness.missingCount,
                      )
                    : ""
                }
                missingFieldsTitle={copy.missingRequiredFields}
              />
            </ProfileSectionCard> */}

            <ProfileSectionCard title={profileLabels.documentInfo.title}>
              {renderApplicantDocumentRow({
                title: profileLabels.documentInfo.cv,
                docKey: APPLICANT_DOCUMENT_KEYS.cv,
                existingDoc: profile?.cv_documents?.[0],
                statusText: copy.notProvided,
              })}
              {renderApplicantDocumentRow({
                title: profileLabels.documentInfo.certificates,
                docKey: APPLICANT_DOCUMENT_KEYS.certificates,
                existingDoc: certificates[0],
                statusText: copy.notProvided,
              })}
              {renderApplicantDocumentRow({
                title: profileLabels.documentInfo.academicQualifications,
                docKey: APPLICANT_DOCUMENT_KEYS.academic,
                existingDoc: profile?.academic_qualification_documents?.[0],
                statusText: copy.notProvided,
              })}
              {renderApplicantDocumentRow({
                title: profileLabels.documentInfo.other,
                docKey: APPLICANT_DOCUMENT_KEYS.other,
                existingDoc: profile?.other_documents?.[0],
                statusText: copy.notProvided,
              })}
            </ProfileSectionCard>

            <ProfileSectionCard title={bankInfoLabels.title}>
              <div className="company-applicant-profile__bank">
                <div className="company-applicant-profile__bank-field">
                  <div className="company-applicant-profile__bank-box">
                    <div
                      className="company-applicant-profile__bank-icon-box"
                      aria-hidden
                    >
                      <CreditCard size={18} strokeWidth={1.75} />
                    </div>
                    <UISelect
                      className={`${FIELD_CLASS} company-applicant-profile__bank-select`}
                      label={bankInfoLabels.bankName}
                      required
                      name="bank"
                      placeholder={applicants.placeholders.selectBank}
                      options={banks || []}
                      value={bank?.id != null ? bank : null}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?.id === value?.id
                      }
                      onChange={(option) => {
                        setBank(option || {});
                        handleFieldValidation("bankId", option?.id ?? "");
                      }}
                      error={Boolean(validationErrors.bankId)}
                      helperText={validationErrors.bankId}
                    />
                  </div>
                </div>

                <div className="company-applicant-profile__bank-field">
                  <UITextField
                    className={LTR_VALUE_FIELD_CLASS}
                    label={bankInfoLabels.IBAN}
                    required
                    name="iban"
                    placeholder={applicants.placeholders.enterIban}
                    value={iban ?? ""}
                    onValueChange={(value) => {
                      setIban(value);
                      handleFieldValidation("iban", value);
                    }}
                    onBlur={(e) => handleFieldValidation("iban", e.target.value)}
                    error={Boolean(validationErrors.iban)}
                    helperText={validationErrors.iban}
                  />
                </div>

                <div className="company-applicant-profile__bank-field">
                  <UITextField
                    className={FIELD_CLASS}
                    label={bankInfoLabels.cardHolder}
                    required
                    name="cardHolderName"
                    placeholder={applicants.placeholders.enterCardHolderName}
                    value={cardHolderName ?? ""}
                    onValueChange={(value) => {
                      setCardHolderName(value);
                      handleFieldValidation("cardHolderName", value);
                    }}
                    onBlur={(e) =>
                      handleFieldValidation("cardHolderName", e.target.value)
                    }
                    error={Boolean(validationErrors.cardHolderName)}
                    helperText={validationErrors.cardHolderName}
                  />
                </div>
              </div>
            </ProfileSectionCard>

            <ProfileSectionCard title={profileLabels.educationInfo.title}>
              <div className="company-applicant-profile__field-grid">
                <UISelect
                  className={FIELD_CLASS}
                  label={educationLabels.filedsOfStudy}
                  required
                  name="studyField"
                  placeholder={applicants.placeholders.selectFieldOfStudy}
                  options={fieldOfStudies || []}
                  value={studyField?.id != null ? studyField : null}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(option) => {
                    setStudyField(option || {});
                    handleFieldValidation("studyFieldId", option?.id ?? "");
                  }}
                  error={Boolean(validationErrors.studyFieldId)}
                  helperText={validationErrors.studyFieldId}
                />
                {isStudyFieldOther ? (
                  <UITextField
                    className={FIELD_CLASS}
                    label={educationLabels.fieldOfStudyName}
                    required
                    name="fieldStudyName"
                    placeholder={applicants.placeholders.enterFieldOfStudy}
                    value={fieldStudyName ?? ""}
                    onValueChange={(value) => {
                      setFieldStudyName(value);
                      handleFieldValidation("fieldStudyName", value);
                    }}
                    onBlur={(e) =>
                      handleFieldValidation("fieldStudyName", e.target.value)
                    }
                    error={Boolean(validationErrors.fieldStudyName)}
                    helperText={validationErrors.fieldStudyName}
                  />
                ) : null}
                <UITextField
                  className={LTR_VALUE_FIELD_CLASS}
                  label={basicLabels.stcNumber}
                  required
                  name="stcNumber"
                  placeholder={applicants.placeholders.enterStcNumber}
                  value={stcNumber ?? ""}
                  onValueChange={(value) => {
                    setStcNumber(value);
                    handleFieldValidation("stcNumber", value);
                  }}
                  onBlur={(e) => handleFieldValidation("stcNumber", e.target.value)}
                  error={Boolean(validationErrors.stcNumber)}
                  helperText={validationErrors.stcNumber}
                />
                <UIRadioGroup
                  className={FIELD_CLASS}
                  name="notify"
                  label={basicLabels.notifiable}
                  required
                  row
                  value={
                    isNotifiable === true
                      ? "true"
                      : isNotifiable === false
                        ? "false"
                        : ""
                  }
                  options={[
                    {
                      label: shared.editRequestBanner?.cancelConfirmDialog?.yes || "Yes",
                      value: "true",
                    },
                    {
                      label: shared.editRequestBanner?.cancelConfirmDialog?.no || "No",
                      value: "false",
                    },
                  ]}
                  onChange={(value) => {
                    const nextValue = value === "true";
                    setIsNotifiable(nextValue);
                    handleFieldValidation("isNotifiable", nextValue);
                  }}
                  error={Boolean(validationErrors.isNotifiable)}
                  helperText={validationErrors.isNotifiable}
                />
              </div>

              <ProfileSubsection
                title={profileLabels.languageInfo.title}
                grid={false}
              >
                <div className="company-applicant-profile__language-list">
                  {userLanguages
                    .filter((row) => !row._destroy)
                    .map((row) => {
                      // A language already picked in another row can't be picked again.
                      const languageOptions = (languages || []).filter(
                        (option) =>
                          option.id === row.language?.id ||
                          !selectedLanguageIds.has(option.id),
                      );

                      return (
                        <div
                          key={row.key}
                          className="company-applicant-profile__language-row"
                        >
                          <UISelect
                            className={FIELD_CLASS}
                            label={profileLabels.languageInfo.language}
                            required
                            name={`language-${row.key}`}
                            placeholder={applicants.placeholders.selectLanguage}
                            options={languageOptions}
                            value={row.language?.id != null ? row.language : null}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            onChange={(option) =>
                              handleLanguageRowChange(row.key, "language", option || null)
                            }
                            error={row.language?.id == null}
                            helperText={
                              row.language?.id == null
                                ? inputsValidations.languageRequired
                                : undefined
                            }
                          />
                          <UISelect
                            className={FIELD_CLASS}
                            label={profileLabels.languageInfo.proficiency}
                            required
                            name={`proficiency-${row.key}`}
                            placeholder={applicants.placeholders.selectProficiency}
                            options={speakingProficiency || []}
                            value={row.proficiency?.value != null ? row.proficiency : null}
                            getOptionLabel={(option) =>
                              resolveProficiencyLabel(
                                option,
                                profileLabels.languageInfo.proficiencyLevels,
                              )
                            }
                            isOptionEqualToValue={(option, value) =>
                              option?.value === value?.value
                            }
                            onChange={(option) =>
                              handleLanguageRowChange(row.key, "proficiency", option || null)
                            }
                            error={row.proficiency?.value == null}
                            helperText={
                              row.proficiency?.value == null
                                ? inputsValidations.generalFieldRequired
                                : undefined
                            }
                          />
                          <div className="company-applicant-profile__language-remove-field">
                            <span
                              className="company-applicant-profile__language-remove-label"
                              aria-hidden="true"
                            >
                              &nbsp;
                            </span>
                            <div className="company-applicant-profile__language-remove-control">
                              <UIButton
                                type="button"
                                variant="destructive"
                                appearance="outline"
                                size="small"
                                className="company-applicant-profile__language-remove"
                                onClick={() => handleRemoveLanguageRow(row.key)}
                                aria-label={profileLabels.languageInfo.removeLanguage}
                              >
                                <UIIcon name="trash" size={20} decorative />
                              </UIButton>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
                <UIButton
                  type="button"
                  variant="primary"
                  appearance="outline"
                  size="large"
                  onClick={handleAddLanguageRow}
                  className="mt-4"
                >
                  {profileLabels.languageInfo.addLanguage}
                </UIButton>
              </ProfileSubsection>
            </ProfileSectionCard>
          </div>
        </div>

        <UIPageFormFooter
          className="edit-applicant-page__footer"
          cancelLabel={shared.cancel}
          submitLabel={applicants.saveChanges}
          onCancel={handleCancel}
          submitType="submit"
          submitDisabled={
            !isFormValid ||
            isSubmitting ||
            isUploadingDocuments ||
            hasIncompleteLanguageRow ||
            isIqamaExpired
          }
        />
      </form>
    </Box>
  );
};

export default EditApplicantProfile;

EditApplicantProfile.propTypes = {
  portalVariant: PropTypes.oneOf(["admin-portal", "company-portal"]),
};

EditApplicantProfile.defaultProps = {
  portalVariant: "admin-portal",
};
